import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ShoppingCart, Lock, Clock, ChevronLeft, ChevronRight, Filter, Package, Tag } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import { productsAPI, registryAPI, productDiscountsAPI, flashOffersAPI, catalogDiscountsAPI, discountsAPI, businessAPI } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

const SearchResults = () => {
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const { isAuthenticated, user } = useAuth();

  const q = searchParams.get('q') || '';
  const category = searchParams.get('category') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const inStockOnly = searchParams.get('inStock') === 'true';

  const [searchInput, setSearchInput] = useState(q);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  // Discount state — keyed by wholesaler id
  const [productDiscounts, setProductDiscounts] = useState({}); // { [wid]: { [pid]: { percentage } } }
  const [customerDiscounts, setCustomerDiscounts] = useState({}); // { [wid]: { percentage } }
  const [catalogDiscounts, setCatalogDiscounts] = useState({}); // { [wid]: [...rawDiscounts] }
  const [flashOfferMap, setFlashOfferMap] = useState({}); // { [pid]: { offerPrice, offerId, offerTitle } }

  // Load categories
  useEffect(() => {
    registryAPI.getCategories(i18n.language).then(res => {
      setCategories(res.data?.data || []);
    }).catch(() => {});
  }, [i18n.language]);

  // Fetch results
  const fetchResults = useCallback(async () => {
    if (!q || q.length < 2) return;
    setLoading(true);
    try {
      const params = { q, page, limit: 20 };
      if (category) params.category = category;
      const res = await productsAPI.crossSearch(params);
      setResults(res.data);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [q, category, page]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Fetch discounts for every wholesaler represented in the results
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'business' || !results?.products?.length) return;

    const uniqueWids = [...new Set(
      results.products
        .map(p => p.wholesaler?._id)
        .filter(Boolean)
        .map(String)
    )];

    let cancelled = false;

    const loadDiscounts = async () => {
      const pdMap = {};
      const cdMap = {};
      const catMap = {};

      let businessId = null;
      try {
        const profileRes = await businessAPI.getProfile();
        businessId = profileRes.data.business?._id;
      } catch { /* silent */ }

      await Promise.all(uniqueWids.map(async (widStr) => {
        try {
          const res = await productDiscountsAPI.getMyDiscounts(widStr);
          if (res.data.success && res.data.discounts && typeof res.data.discounts === 'object') {
            pdMap[widStr] = res.data.discounts;
          }
        } catch { /* silent */ }

        if (businessId) {
          try {
            const res = await discountsAPI.getCustomerDiscountsForBusiness(businessId, widStr);
            if (res.data.success && res.data.hasDiscounts) {
              const totalPct = res.data.discounts.reduce((sum, d) => sum + (d.percentage || 0), 0);
              cdMap[widStr] = { percentage: Math.min(totalPct, 100) };
            }
          } catch { /* silent */ }
        }

        try {
          const res = await catalogDiscountsAPI.getForWholesaler(widStr);
          if (res.data.discounts?.length > 0) catMap[widStr] = res.data.discounts;
        } catch { /* silent */ }
      }));

      if (cancelled) return;
      setProductDiscounts(pdMap);
      setCustomerDiscounts(cdMap);
      setCatalogDiscounts(catMap);

      // Flash offers — one global fetch
      try {
        const foRes = await flashOffersAPI.getAvailable();
        const foMap = {};
        (foRes.data.offers || []).forEach(offer => {
          (offer.items || []).forEach(item => {
            if (item.productId && item.offerPrice != null) {
              foMap[String(item.productId)] = {
                offerPrice: item.offerPrice,
                offerId: offer._id,
                offerTitle: offer.title
              };
            }
          });
        });
        if (!cancelled) setFlashOfferMap(foMap);
      } catch { /* silent */ }
    };

    loadDiscounts();
    return () => { cancelled = true; };
  }, [results, isAuthenticated, user]);

  // Compute effective price for a product card
  const getCatalogDiscountPct = (product, wholesalerId) => {
    const discounts = catalogDiscounts[String(wholesalerId)];
    if (!discounts?.length) return 0;
    let totalPct = 0;
    discounts.forEach(d => {
      if (d.type === 'flat') {
        totalPct += d.percentage || 0;
      } else if (d.type === 'targeted') {
        const matches =
          (d.targetType === 'category' && (product.category || '').toLowerCase() === (d.targetValue || '').toLowerCase()) ||
          (d.targetType === 'brand' && (product.brand || '').toLowerCase() === (d.targetValue || '').toLowerCase());
        if (matches) totalPct += d.targetPercentage || 0;
      }
    });
    return Math.min(totalPct, 90);
  };

  const computeCardPricing = (product) => {
    const wid = String(product.wholesaler?._id || '');
    const pid = String(product._id);
    const originalPrice = product.pricing?.unitPrice || product.pricing?.basePrice || 0;
    const pd = productDiscounts[wid]?.[pid] || null;
    const fo = flashOfferMap[pid];
    const cdPct = customerDiscounts[wid]?.percentage || 0;
    const catPct = getCatalogDiscountPct(product, wid);

    let effectivePrice = originalPrice;
    let kind = null;
    if (fo) {
      // Flash offer = final price, no other discounts on top
      effectivePrice = fo.offerPrice;
      kind = 'flash';
    } else if (pd) {
      const effectivePct = Math.min(Math.max(pd.percentage, cdPct) + catPct, 100);
      effectivePrice = Math.round(originalPrice * (1 - effectivePct / 100) * 100) / 100;
      kind = 'product';
    } else if (catPct > 0) {
      const totalPct = Math.min(catPct + cdPct, 100);
      effectivePrice = Math.round(originalPrice * (1 - totalPct / 100) * 100) / 100;
      kind = 'catalog';
    } else if (cdPct > 0) {
      effectivePrice = Math.round(originalPrice * (1 - cdPct / 100) * 100) / 100;
      kind = 'customer';
    }
    return { originalPrice, effectivePrice, hasAnySaving: effectivePrice < originalPrice, kind, flashOffer: fo };
  };

  // Sync search input with URL param
  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.length >= 2) {
      setSearchParams({ q: searchInput, ...(category && { category }), page: '1' });
    }
  };

  const handleCategoryChange = (cat) => {
    const params = { q };
    if (cat) params.category = cat;
    if (inStockOnly) params.inStock = 'true';
    params.page = '1';
    setSearchParams(params);
  };

  const handleInStockToggle = () => {
    const params = { q };
    if (category) params.category = category;
    if (!inStockOnly) params.inStock = 'true';
    params.page = '1';
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = { q, page: String(newPage) };
    if (category) params.category = category;
    if (inStockOnly) params.inStock = 'true';
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const handleAddToCart = (product) => {
    if (!product.pricing || !product.wholesaler) return;
    const fo = flashOfferMap[String(product._id)];
    addToCart({
      _id: product._id,
      name: product.name,
      pricing: product.pricing,
      images: product.images,
      wholesalerId: product.wholesaler._id,
      wholesalerName: product.wholesaler.companyName,
      quantity: product.pricing.minOrderQuantity || 1,
      ...(fo && {
        flashOfferPrice: fo.offerPrice,
        flashOfferId: fo.offerId,
        flashOfferTitle: fo.offerTitle
      })
    });
  };

  const stockDot = (status) => {
    if (status === 'in_stock') return 'bg-green-500';
    if (status === 'low_stock') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Filter in-stock on client side (products are already fetched)
  const displayProducts = inStockOnly
    ? (results?.products || []).filter(p => p.stockStatus === 'in_stock')
    : (results?.products || []);

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 mt-14 lg:mt-0">
        {/* Search Header */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('common:productSearch.placeholder', 'Search products across wholesalers...')}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
            >
              {t('common:productSearch.searchButton', 'Search')}
            </button>
          </div>
        </form>

        {/* Results info */}
        {q && results && (
          <p className="text-sm text-gray-600 mb-4">
            {t('common:productSearch.resultsFor', 'Results for')}{' '}
            <span className="font-medium text-gray-900">"{q}"</span>
            {results.count > 0 && (
              <span className="text-gray-500"> — {t('common:productSearch.resultCount', '{{count}} products', { count: results.count })}</span>
            )}
          </p>
        )}

        {/* Filters Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={category}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('common:productSearch.allCategories', 'All Categories')}</option>
            {categories.map(cat => (
              <option key={cat.key} value={cat.key}>{cat.label}</option>
            ))}
          </select>

          <button
            onClick={handleInStockToggle}
            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
              inStockOnly
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('common:productSearch.inStockOnly', 'In Stock Only')}
          </button>
        </div>

        {/* Private Catalog Teasers */}
        {results?.privateTeasers?.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-medium text-amber-800">
                {t('common:productSearch.privateCatalogs', 'Private catalogs with matching products')}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {results.privateTeasers.map((teaser) => (
                <div
                  key={teaser.wholesaler._id}
                  className="flex items-center gap-3 bg-white rounded-lg p-3 border border-amber-100"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {teaser.wholesaler.logo ? (
                      <img src={teaser.wholesaler.logo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Lock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => navigate(`/wholesalers/${teaser.wholesaler._id}`)}
                      className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate block text-left"
                    >
                      {teaser.wholesaler.companyName}
                    </button>
                    <p className="text-xs text-gray-500">
                      {teaser.matchCount} {t('common:productSearch.productsMatch', 'products match')}
                    </p>
                  </div>
                  {teaser.hasPendingRequest ? (
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {t('common:productSearch.requestPending', 'Pending')}
                    </span>
                  ) : (
                    <button
                      onClick={() => navigate(`/wholesalers/${teaser.wholesaler._id}`)}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 whitespace-nowrap"
                    >
                      {t('common:productSearch.requestAccess', 'Request access')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Product Grid */}
        {!loading && displayProducts.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {displayProducts.map((product) => {
              const { originalPrice, effectivePrice, hasAnySaving, kind } = computeCardPricing(product);
              const pct = hasAnySaving ? Math.round(((originalPrice - effectivePrice) / originalPrice) * 100) : 0;
              const priceColorClass =
                kind === 'flash' ? 'text-red-600' :
                kind === 'product' ? 'text-purple-700' :
                kind === 'catalog' ? 'text-teal-700' :
                kind === 'customer' ? 'text-emerald-600' :
                'text-gray-900';
              const badgeClass =
                kind === 'flash' ? 'bg-gradient-to-r from-red-500 to-orange-500' :
                kind === 'product' ? 'bg-purple-600' :
                kind === 'catalog' ? 'bg-teal-600' :
                'bg-emerald-600';
              return (
              <div
                key={product._id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate(`/products/${product._id}`)}
              >
                {/* Image */}
                <div className="aspect-[4/3] bg-gray-50 relative overflow-hidden">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
                      <Package className="w-6 h-6" />
                    </div>
                  )}
                  {/* Stock dot */}
                  <span className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${stockDot(product.stockStatus)} ring-2 ring-white`} />
                  {/* Discount badge */}
                  {hasAnySaving && (
                    <div className="absolute top-1 left-1 z-10">
                      {kind === 'flash' ? (
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold text-white rounded-md shadow-sm ${badgeClass}`}>
                          ⚡ -{pct}%
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold text-white rounded-md shadow-sm ${badgeClass}`}>
                          <Tag className="w-2.5 h-2.5" />
                          -{pct}%
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-2">
                  <h3 className="text-xs font-medium text-gray-900 line-clamp-2 leading-snug">{product.name}</h3>

                  {/* Wholesaler badge */}
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/wholesalers/${product.wholesaler?._id}`); }}
                    className="flex items-center gap-1 mt-1 hover:text-blue-600"
                  >
                    {product.wholesaler?.logo && (
                      <img src={product.wholesaler.logo} alt="" className="w-3.5 h-3.5 rounded-full" />
                    )}
                    <span className="text-[10px] text-gray-500 truncate">{product.wholesaler?.companyName}</span>
                  </button>

                  {/* Price */}
                  <div className="mt-1.5 flex items-center justify-between">
                    {product.pricing ? (
                      hasAnySaving ? (
                        <div className="flex items-baseline gap-1 flex-wrap min-w-0">
                          <span className={`text-xs font-bold ${priceColorClass}`}>€{effectivePrice.toFixed(2)}</span>
                          <span className="text-[10px] text-gray-400 line-through">€{originalPrice.toFixed(2)}</span>
                          <span className="text-[10px] text-gray-500">/{product.pricing.unit}</span>
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-gray-900">
                          €{originalPrice.toFixed(2)}<span className="text-[10px] text-gray-500 font-normal">/{product.pricing.unit}</span>
                        </span>
                      )
                    ) : (
                      <span className="text-[10px] text-gray-400 italic">
                        {t('common:productSearch.priceOnAgreement', 'Price on agreement')}
                      </span>
                    )}

                    {product.pricing && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded flex-shrink-0"
                        title={t('common:productSearch.addToCart', 'Add to cart')}
                      >
                        <ShoppingCart className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && displayProducts.length === 0 && results && results.privateTeasers?.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">{t('common:productSearch.noResults', 'No products found')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('common:productSearch.noResultsHint', 'Try a different search term')}</p>
          </div>
        )}

        {/* Pagination */}
        {results && results.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 px-3">
              {t('common:pagination.page', 'Page')} {page} / {results.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= results.totalPages}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchResults;
